import React, { useState, useEffect, ChangeEvent } from 'react';
import { Input } from '@storybook';
import { useNetwork } from 'hooks';
import { API_URL } from 'constant';

import './custom-crd-question.scss';

interface ICrdCheckResponse {
	message: string;
	data: {
		isAlreadyExists: boolean;
		result: string;
	}[];
}

interface ICrdValidationInputProps {
	value?: string;
	onChange?: (value: string, exists: boolean, validationMessage?: string) => void;
	placeholder?: string;
	disabled?: boolean;
	readOnly?: boolean; // Prevents API calls in display/read-only mode
	initialValidationState?: { isUnique: boolean | null; validationMessage: string } | null;
}

const DEBOUNCE_DELAY = 1500;
const MIN_INPUT_LENGTH = 3; 
const SUCCESS_MESSAGE = (crdNumber: string) => 
	`The firm associated with CRD/SEC Number ${crdNumber} is already registered on the platform. Please continue with your Broker-Dealer account setup.`;
const ERROR_MESSAGE = 
	'The CRD/SEC Number you entered does not match any firm currently registered on the platform. Please select one of the options below to start a new onboarding.';
const VALIDATION_FAILED_MESSAGE = 'Validation failed. Please try again.';

export const CrdValidationInput: React.FC<ICrdValidationInputProps> = ({
	value = '',
	onChange,
	placeholder = 'Enter CRD/SEC Number',
	disabled = false,
	readOnly = false,
	initialValidationState = null,
}) => {
	const { get } = useNetwork({ updateState: false });
	const [inputValue, setInputValue] = useState<string>(value);
	const [isValidating, setIsValidating] = useState<boolean>(false);
	const [validationMessage, setValidationMessage] = useState<string>(
		initialValidationState?.validationMessage || ''
	);
	const [isUnique, setIsUnique] = useState<boolean | null>(
		initialValidationState?.isUnique ?? null
	);

	useEffect(() => {
		setInputValue(value);
		// Clear validation state when value is cleared from parent
		if (!value?.trim()) {
			setIsValidating(false);
			setIsUnique(null);
			setValidationMessage('');
		}
	}, [value]);

	// Restore validation state when initialValidationState changes (e.g., in display mode)
	// But don't restore if input is currently empty
	useEffect(() => {
		if (initialValidationState && inputValue?.trim()) {
			setValidationMessage(initialValidationState.validationMessage || '');
			setIsUnique(initialValidationState.isUnique ?? null);
		}
	}, [initialValidationState, inputValue]);

	/**
	 * Handle input change with debouncing
	 * Optimized to prevent unnecessary API calls
	 */
	useEffect(() => {
		
		// Don't make API calls in read-only/display mode
		if (readOnly) {
			return;
		}

		// Don't validate if input is empty or too short
		if (!inputValue?.trim() || inputValue.trim().length < MIN_INPUT_LENGTH) {
			setIsValidating(false);
			setIsUnique(null);
			setValidationMessage('');
			onChange?.(inputValue, false);
			return;
		}

		// Set validating state immediately
		setIsValidating(true);
		setValidationMessage('');

		// Debounce the API call
		const debounceTimer = setTimeout(async () => {
			try {
				const apiUrl = `${API_URL.CRD_CHECK}?crdNumber=${encodeURIComponent(inputValue)}`;
				const data: ICrdCheckResponse = await get(apiUrl);

				// Parse API response
				if (!data?.data?.[0]) {
					throw new Error('Invalid API response format');
				}

				const exists = data.data[0].isAlreadyExists === true;
				const message = exists ? SUCCESS_MESSAGE(inputValue) : ERROR_MESSAGE;

				setIsValidating(false);
				setIsUnique(exists);
				setValidationMessage(message);
				onChange?.(inputValue, exists, message);

			} catch (error: any) {
				setIsValidating(false);
				setIsUnique(null);
				setValidationMessage(VALIDATION_FAILED_MESSAGE);
				onChange?.(inputValue, false, VALIDATION_FAILED_MESSAGE);
			}
		}, DEBOUNCE_DELAY);

		// Cleanup function to cancel pending API calls
		return () => {
			clearTimeout(debounceTimer);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inputValue, readOnly]);

	/**
	 * Handle input change
	 */
	const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
		const newValue = e.target.value;
		setInputValue(newValue);
	};

	/**
	 * Get error state and message for Input component
	 */
	const getErrorState = (): { isError: boolean; errorMessage: string } => {
		// Don't show errors if input is too short or empty
		if (!inputValue?.trim() || inputValue.trim().length < MIN_INPUT_LENGTH) {
			return { isError: false, errorMessage: '' };
		}

		// Show validating state
		if (isValidating) {
			return { isError: false, errorMessage: '' };
		}

		// Show error message
		if (isUnique === false && validationMessage) {
			return { isError: true, errorMessage: validationMessage };
		}

		// Show success message (not an error, but we'll handle this separately)
		return { isError: false, errorMessage: '' };
	};

	/**
	 * Get CSS class for custom styling based on validation state
	 */
	const getCustomClassName = (): string => {
		// Don't show validation styling if input is too short
		if (!inputValue?.trim() || inputValue.trim().length < MIN_INPUT_LENGTH) {
			return '';
		}

		if (isValidating) {
			return 'custom-crd-question__input--validating';
		} else if (isUnique === true) {
			return 'custom-crd-question__input--success';
		}
		return '';
	};

	/**
	 * Render success or validating message (since Input only shows errors)
	 */
	const renderAdditionalMessage = (): JSX.Element | null => {
		// Show minimum length hint when input is too short (but not empty)
		if (inputValue && inputValue.trim().length > 0 && inputValue.trim().length < MIN_INPUT_LENGTH) {
			return (
				<div className="custom-crd-question__message custom-crd-question__message--info">
					Please enter at least {MIN_INPUT_LENGTH} digits
				</div>
			);
		}

		// Don't show validation/success messages if input is too short or empty
		if (!inputValue?.trim() || inputValue.trim().length < MIN_INPUT_LENGTH) {
			return null;
		}

		// Show validating state
		if (isValidating) {
			return (
				<div className="custom-crd-question__message custom-crd-question__message--validating">
					Validating...
				</div>
			);
		}

		// Show success message
		if (isUnique === true && validationMessage) {
			return (
				<div className="custom-crd-question__message custom-crd-question__message--success">
					{validationMessage}
				</div>
			);
		}

		return null;
	};

	const { isError, errorMessage } = getErrorState();

	return (
		<div className="custom-crd-question__container">
			<Input
				label=""
				inputType="number"
				placeholder={placeholder}
				handleChange={handleInputChange}
				value={inputValue}
				disabled={disabled}
				isError={isError}
				errorMessage={errorMessage}
				className={getCustomClassName()}
			/>
			{renderAdditionalMessage()}
		</div>
	);
};

export default CrdValidationInput;

